import { Navigate, Outlet, useLocation } from "react-router-dom";
import { tokenService } from "@/services/auth/token.service";
import { AuthLayout } from "./AuthLayout";
import { routes } from "../navigation";

/** Router `location.state` when redirecting after auth (e.g. `from` deep link). */
export type AuthLayoutLocationState = { from?: string };

/**
 * Wrapper that combines authentication route logic with AuthLayout
 * Redirects authenticated users away, otherwise renders AuthLayout with children
 */
export const AuthLayoutWrapper = () => {
  const isAuthenticated = tokenService.hasToken();
  const location = useLocation();

  if (isAuthenticated) {
    // Protest/properties restoration uses imperative `navigate(..., { state })` from
    // login / OTP VMs. Declarative `<Navigate />` here raced those updates and often
    // sent users to `/` without route state. Defer wrapper redirects only while a
    // properties restore is clearly pending so VMs remain the redirect authority.
    const protestFlowRaw = sessionStorage.getItem("property_protest_flow");
    const hasPersistedProtestFlow = typeof protestFlowRaw === "string" && protestFlowRaw.length > 0;
    const fromState = (location.state as AuthLayoutLocationState | null | undefined)?.from;
    const restorePending =
      hasPersistedProtestFlow || (typeof fromState === "string" && fromState === routes.properties);

    if (restorePending) {
      return (
        <AuthLayout>
          <Outlet />
        </AuthLayout>
      );
    }

    if (typeof fromState === "string" && fromState && fromState !== routes.login) {
      return <Navigate to={fromState} replace />;
    }

    return <Navigate to={routes.home} replace />;
  }

  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
};
